import * as React from 'react';
import { useCallback } from 'react';
import styled from 'styled-components';
import Immutable from 'immutable';

import FieldSelect from 'data-lake/components/FieldSelect';
import StickyBottomActions from 'views/components/widgets/StickyBottomActions';
import SaveOrCancelButtons from 'views/components/widgets/SaveOrCancelButtons';
import DescriptionBox from 'views/components/aggregationbuilder/DescriptionBox';
import FieldsConfiguration from 'views/components/widgets/FieldsConfiguration';
import type FieldTypeMapping from 'views/logic/fieldtypes/FieldTypeMapping';
import { Alert, Button } from 'components/bootstrap';
import { WAHREHOUSE_PREVIEW_FORM_ID } from 'data-lake/preview/storage-types/internal/SearchBar';
import { Icon } from 'components/common';
import type DataLakeWidgetConfig from 'data-lake/logic/DataLakeWidgetConfig';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import hasCustomFields from 'data-lake/preview/utils/hasCustomFields';
import useReexecuteNeedWarning from 'data-lake/preview/hooks/useReexecuteNeedWarning';
import usePreviewLayout from 'data-lake/preview/hooks/usePreviewLayout';
import useFieldTypes from 'data-lake/hooks/useFieldTypes';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import useReexecuteSearch from 'data-lake/preview/hooks/useReexecuteSearch';

import DataLakeFieldsContext from './contexts/DataLakeFieldsContext';

const Container = styled.div`
  height: 100%;
  width: 100%;
  padding-bottom: 15px;
  display: flex;
`;

const Col = styled.div`
  height: 100%;
`;

const ControlsCol = styled(Col)`
  min-width: 315px;
  max-width: 500px;
  flex: 1.2;
  padding-right: 5px;
  margin-right: 10px;
`;

const LogViewCol = styled(Col)`
  flex: 3;
  overflow-y: hidden;
`;

// eslint-disable-next-line react/require-default-props
const LogViewEditFieldSelect = (props: React.ComponentProps<typeof FieldSelect>) => {
  const { widgetEdit } = usePreviewLayout();

  return <FieldSelect {...props} allowCreate={widgetEdit?.fieldsCreationAllowed} />;
};

type Props = React.PropsWithChildren<{
  config: DataLakeWidgetConfig;
  onCancel: () => void;
  onChange: (newConfig: DataLakeWidgetConfig) => void;
  isFetching: boolean;
  configBeforeEdit: DataLakeWidgetConfig;
}>;

const DataLakeLogViewEdit = ({
  children = undefined,
  config,
  onChange,
  onCancel,
  isFetching,
  configBeforeEdit,
}: Props) => {
  const sendTelemetry = useSendTelemetry();
  const { data: defaultFields } = useFieldTypes();
  const reexecuteNeedWarning = useReexecuteNeedWarning(config, configBeforeEdit, defaultFields);
  const { widgetEdit } = usePreviewLayout();
  const { selectedDataLake } = useSelectedDataLake();
  const reexecuteSearch = useReexecuteSearch();

  const onChangeFields = useCallback(
    (newFields: Array<string>) => {
      const newFieldsSet = Immutable.OrderedSet(newFields);

      sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW[selectedDataLake.type].WIDGET_FIELDS_UPDATE, {
        app_pathname: '/data-storage/preview',
        app_section: 'log-view-widget',
        event_details: {
          fields: newFields,
          hasCustomFields: hasCustomFields(newFieldsSet, defaultFields),
          data_lake_storage_type: selectedDataLake.storageType,
        },
      });

      onChange(config.toBuilder().fields(Immutable.OrderedSet(newFields)).build());
    },
    [config, defaultFields, onChange, selectedDataLake.storageType, selectedDataLake.type, sendTelemetry],
  );

  return (
    <DataLakeFieldsContext.Provider value={Immutable.List<FieldTypeMapping>(config.fields)}>
      <Container>
        <ControlsCol>
          <StickyBottomActions actions={<SaveOrCancelButtons onCancel={onCancel} />} alignActionsAtBottom>
            <DescriptionBox description="Field Selection and Order">
              {widgetEdit?.showFieldSelect && (
                <FieldsConfiguration
                  onChange={onChangeFields}
                  fieldSelect={LogViewEditFieldSelect}
                  createSelectPlaceholder={
                    widgetEdit?.fieldsCreationAllowed ? 'Select or type in any field name' : 'Select field name'
                  }
                  selectSize="normal"
                  selectedFields={config.fields.toArray()}
                />
              )}
              {widgetEdit?.fieldSelectInfo}
            </DescriptionBox>
            {reexecuteNeedWarning && (
              <Alert bsStyle="warning">
                <p>{reexecuteNeedWarning}</p>
                <Button
                  type="submit"
                  bsSize="xsmall"
                  bsStyle="primary"
                  disabled={isFetching}
                  onClick={reexecuteSearch}
                  form={WAHREHOUSE_PREVIEW_FORM_ID}>
                  <Icon name="search" />
                  Re-execute search
                </Button>
              </Alert>
            )}
            {widgetEdit?.formInfo}
          </StickyBottomActions>
        </ControlsCol>
        <LogViewCol>{children}</LogViewCol>
      </Container>
    </DataLakeFieldsContext.Provider>
  );
};

export default DataLakeLogViewEdit;
